<template>
  <div
    ref="containerRef"
    class="tw:w-full tw:sm:w-5/6 tw:lg:w-3/4 tw:mx-auto tw:pt-12 tw:sm:py-12 tw:sm:px-8 tw:flex tw:flex-col tw:gap-8 tw:sm:gap-8 tw:rounded-2xl tw:relative tw:overflow-hidden"
    role="region"
    aria-labelledby="benefits-title"
  >
    <div class="tw:flex tw:flex-col tw:relative tw:z-10">
      <h2
        id="benefits-title"
        class="tw:heading-2 tw:text-center tw:sm:text-left tw:font-normal tw:text-[1.45rem]"
        v-html="asideTitleHtml"
      ></h2>
      <p class="tw:body-1 tw:text-center tw:sm:text-left tw:text-default-700 tw:leading-relaxed">
        {{ trans._register.platforms.aside.subtitle }}
      </p>
    </div>
    <div
      class="tw:flex tw:flex-col tw:flex-wrap tw:sm:flex-row tw:items-stretch tw:justify-between tw:gap-4 tw:sm:gap-4 tw:relative tw:z-10"
      role="list"
    >
      <div
        class="tw:border tw:border-default-200 tw:bg-white tw:rounded-xl tw:p-5 tw:text-center tw:flex-1 tw:w-full tw:sm:w-auto tw:flex tw:flex-col tw:justify-center tw:min-h-[100px] tw:shadow-xs tw:hover:shadow-lg tw:transition-all tw:duration-300 tw:hover:-translate-y-1 tw:relative tw:group"
        role="listitem"
      >
        <div
          class="tw:absolute tw:top-3 tw:right-3 tw:w-8 tw:h-8 tw:bg-blue-100 tw:rounded-full tw:flex tw:items-center tw:justify-center tw:opacity-70 tw:group-hover:opacity-100 tw:transition-opacity"
        >
          <FontAwesomeIcon icon="fa-light fa-chart-line" class="tw:text-blue-600 tw:w-4 tw:h-4" aria-hidden="true" />
        </div>
        <p
          class="tw:heading-1 tw:text-black tw:mb-2 tw:font-bold tw:text-3xl tw:transform tw:transition-all tw:duration-300"
          :aria-label="trans._register.platforms.aside.metrics.retention.text"
        >
          {{ formatRetentionValue(animatedRetention) }}
        </p>
        <p class="tw:body-2 tw:text-gray-600 tw:font-medium">
          {{ trans._register.platforms.aside.metrics.retention.text }}
        </p>
      </div>
      <div
        class="tw:border tw:border-default-200 tw:bg-white tw:rounded-xl tw:p-5 tw:text-center tw:flex-1 tw:w-full tw:sm:w-auto tw:flex tw:flex-col tw:justify-center tw:min-h-[100px] tw:shadow-xs tw:hover:shadow-lg tw:transition-all tw:duration-300 tw:hover:-translate-y-1 tw:relative tw:group"
        role="listitem"
      >
        <div
          class="tw:absolute tw:top-3 tw:right-3 tw:w-8 tw:h-8 tw:bg-green-100 tw:rounded-full tw:flex tw:items-center tw:justify-center tw:opacity-70 tw:group-hover:opacity-100 tw:transition-opacity"
        >
          <FontAwesomeIcon icon="fa-light fa-dollar-sign" class="tw:text-green-600 tw:w-4 tw:h-4" aria-hidden="true" />
        </div>
        <p
          class="tw:heading-1 tw:text-black tw:mb-2 tw:font-bold tw:text-3xl tw:transform tw:transition-all tw:duration-300"
          :aria-label="trans._register.platforms.aside.metrics.aov.text"
        >
          +{{ formatAovValue(animatedAov) }}
        </p>
        <p class="tw:body-2 tw:text-gray-600 tw:font-medium">{{ trans._register.platforms.aside.metrics.aov.text }}</p>
      </div>
      <div
        class="tw:border tw:border-default-200 tw:bg-white tw:rounded-xl tw:p-5 tw:text-center tw:flex-1 tw:w-full tw:sm:w-auto tw:flex tw:flex-col tw:justify-center tw:min-h-[100px] tw:shadow-xs tw:hover:shadow-lg tw:transition-all tw:duration-300 tw:hover:-translate-y-1 tw:relative tw:group"
        role="listitem"
      >
        <div
          class="tw:absolute tw:top-3 tw:right-3 tw:w-8 tw:h-8 tw:bg-purple-100 tw:rounded-full tw:flex tw:items-center tw:justify-center tw:opacity-70 tw:group-hover:opacity-100 tw:transition-opacity"
        >
          <FontAwesomeIcon
            icon="fa-solid fa-shopping-cart"
            class="tw:text-purple-600 tw:w-4 tw:h-4"
            aria-hidden="true"
          />
        </div>
        <p
          class="tw:heading-1 tw:text-black tw:mb-2 tw:font-bold tw:text-3xl tw:transform tw:transition-all tw:duration-300"
          :aria-label="trans._register.platforms.aside.metrics.moreProducts.text"
        >
          +{{ formatMoreProductsValue(animatedMoreProducts) }}
        </p>
        <p class="tw:body-2 tw:text-gray-600 tw:font-medium">
          {{ trans._register.platforms.aside.metrics.moreProducts.text }}
        </p>
      </div>
    </div>
    <div class="tw:flex tw:flex-col tw:gap-6 tw:relative tw:z-10">
      <div
        class="tw:flex tw:flex-col tw:sm:flex-row tw:gap-4 tw:sm:gap-6 tw:bg-white tw:rounded-2xl tw:p-6 tw:shadow-md tw:hover:shadow-xl tw:transition-all tw:duration-300 tw:relative tw:overflow-hidden"
        role="article"
        aria-labelledby="review-title"
      >
        <div
          class="tw:absolute tw:top-0 tw:right-0 tw:w-20 tw:h-20 tw:bg-gradient-to-bl tw:from-orange-100 tw:to-transparent tw:rounded-full tw:opacity-40 tw:-mr-10 tw:-mt-10"
        ></div>

        <div
          class="tw:rounded-full tw:w-16 tw:h-16 tw:flex tw:items-center tw:justify-center tw:shrink-0 tw:mx-auto tw:sm:mx-0 tw:bg-white tw:shadow-md"
          aria-hidden="true"
        >
          <img
            src="https://d3vhsxl1pwzf0p.cloudfront.net/images/g2Logo.png"
            alt="Logo G2"
            loading="lazy"
            class="tw:w-10 tw:h-10 tw:object-contain"
          />
        </div>
        <div class="tw:flex tw:flex-col tw:gap-4 tw:text-center tw:sm:text-left tw:flex-1">
          <div class="tw:flex tw:flex-col tw:gap-2">
            <div
              class="tw:flex tw:items-center tw:gap-2 tw:justify-center tw:sm:justify-start"
              role="img"
              aria-label="5 out of 5 stars"
            >
              <div class="tw:flex tw:gap-1 tw:items-center">
                <FontAwesomeIcon
                  icon="fa-solid fa-star"
                  class="tw:text-[#FF6B35] tw:w-5 tw:h-5 tw:drop-shadow-xs"
                  aria-hidden="true"
                />
                <FontAwesomeIcon
                  icon="fa-solid fa-star"
                  class="tw:text-[#FF6B35] tw:w-5 tw:h-5 tw:drop-shadow-xs"
                  aria-hidden="true"
                />
                <FontAwesomeIcon
                  icon="fa-solid fa-star"
                  class="tw:text-[#FF6B35] tw:w-5 tw:h-5 tw:drop-shadow-xs"
                  aria-hidden="true"
                />
                <FontAwesomeIcon
                  icon="fa-solid fa-star"
                  class="tw:text-[#FF6B35] tw:w-5 tw:h-5 tw:drop-shadow-xs"
                  aria-hidden="true"
                />
                <FontAwesomeIcon
                  icon="fa-solid fa-star"
                  class="tw:text-[#FF6B35] tw:w-5 tw:h-5 tw:drop-shadow-xs"
                  aria-hidden="true"
                />
              </div>
            </div>
          </div>
          <div class="tw:bg-white tw:rounded-lg">
            <p class="tw:body-2 tw:text-gray-800 tw:w-full tw:italic tw:leading-relaxed" id="review-title">
              "{{ currentReviewContent.description }}"
            </p>
          </div>
          <div class="tw:flex tw:flex-col tw:gap-1">
            <p class="tw:body-1--bold tw:text-gray-900 tw:font-semibold">
              {{ currentReviewContent.name }}
            </p>
            <p class="tw:body-2 tw:text-gray-600">{{ currentReviewContent.position }}</p>
          </div>
        </div>
      </div>
      <div class="tw:flex tw:flex-col tw:gap-2 tw:w-full tw:items-center tw:rounded-xl">
        <div
          class="tw:flex tw:items-center tw:gap-2 tw:justify-center"
          role="img"
          aria-label="Rating 4.8 out of 5 stars"
        >
          <div class="tw:flex tw:gap-1 tw:items-center">
            <FontAwesomeIcon
              icon="fa-solid fa-star"
              class="tw:text-[#FF6B35] tw:w-5 tw:h-5 tw:drop-shadow-xs"
              aria-hidden="true"
            />
            <FontAwesomeIcon
              icon="fa-solid fa-star"
              class="tw:text-[#FF6B35] tw:w-5 tw:h-5 tw:drop-shadow-xs"
              aria-hidden="true"
            />
            <FontAwesomeIcon
              icon="fa-solid fa-star"
              class="tw:text-[#FF6B35] tw:w-5 tw:h-5 tw:drop-shadow-xs"
              aria-hidden="true"
            />
            <FontAwesomeIcon
              icon="fa-solid fa-star"
              class="tw:text-[#FF6B35] tw:w-5 tw:h-5 tw:drop-shadow-xs"
              aria-hidden="true"
            />
            <FontAwesomeIcon
              icon="fa-solid fa-star"
              class="tw:text-[#FF6B35] tw:w-5 tw:h-5 tw:drop-shadow-xs"
              aria-hidden="true"
            />
          </div>
          <div class="tw:flex tw:items-center tw:gap-1 tw:pt-1">
            <p class="tw:body-1--bold tw:text-gray-900 tw:transform tw:transition-all tw:duration-300">4.8</p>
            <p class="tw:body-1 tw:text-gray-600">{{ trans._register.platforms.aside.review.outOf }}</p>
            <p class="tw:body-1--bold tw:text-gray-900">5</p>
          </div>
        </div>
        <p class="tw:caption tw:text-gray-500 tw:font-medium">241 {{ trans._register.platforms.aside.review.title }}</p>
      </div>
      <div class="tw:flex tw:flex-col tw:relative tw:z-10">
        <h3 class="tw:heading-3 tw:text-center tw:text-default-700 tw:font-medium">
          {{ trans._register.platforms.aside.trustedBy }}
        </h3>
        <div class="tw:flex tw:items-center tw:justify-center tw:gap-6 tw:sm:gap-6 tw:opacity-65">
          <div
            v-for="(partner, index) in partnersLogos"
            :key="index"
            :class="`tw:flex tw:items-center tw:justify-center tw:transition-all tw:duration-300`"
          >
            <img
              :src="partner.src"
              :alt="partner.alt"
              loading="lazy"
              :class="`${partner.width} ${partner.height} tw:object-contain tw:grayscale tw:hover:grayscale-0 tw:transition-all tw:duration-300`"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { ref, onMounted, nextTick, onUnmounted, computed } from "vue";
import { useRoute } from "vue-router";

const trans = (window as any)._trans;

const animatedRetention = ref(0);
const animatedAov = ref(0);
const animatedMoreProducts = ref(0);

const containerRef = ref<HTMLElement>();

const getQueryLanguage = (): string | null => {
  try {
    const v = new URLSearchParams(window.location.search).get("lang");
    return v ? v.toLowerCase() : "pl";
  } catch {
    return "pl";
  }
};

const route = useRoute();
const currentReviewContent = computed(() => {
  const isFromRoute = route.path === "/form";

  if (isFromRoute) {
    return {
      description: trans._register.platforms.aside.review.alternativeDescription,
      name: trans._register.platforms.aside.review.alternativeName,
      position: trans._register.platforms.aside.review.alternativePosition,
    };
  }

  return {
    description: trans._register.platforms.aside.review.description,
    name: trans._register.platforms.aside.review.name,
    position: trans._register.platforms.aside.review.position,
  };
});

// Partners logos configuration based on language (z obsługą ?lang=)
const partnersLogos = computed(() => {
  const logosConfig = {
    pl: [
      {
        src: "https://d3vhsxl1pwzf0p.cloudfront.net/shopLogos/xiaomi.svg",
        alt: "Mi Home",
        width: "tw:w-16",
        height: "tw:h-12",
      },
      {
        src: "https://d3vhsxl1pwzf0p.cloudfront.net/shopLogos/rylko.webp",
        alt: "Ryłko",
        width: "tw:w-20",
        height: "tw:h-16",
      },
      {
        src: "https://d3vhsxl1pwzf0p.cloudfront.net/shopLogos/ispot.png",
        alt: "iSpot",
        width: "tw:w-24",
        height: "tw:h-16",
      },
      {
        src: "https://d3vhsxl1pwzf0p.cloudfront.net/shopLogos/bielenda.webp",
        alt: "Bielenda",
        width: "tw:w-20",
        height: "tw:h-16",
      },
    ],
    en: [
      {
        src: "https://d3vhsxl1pwzf0p.cloudfront.net/shopLogos/xiaomi.svg",
        alt: "Mi Home",
        width: "tw:w-16",
        height: "tw:h-12",
      },
      {
        src: "https://d3vhsxl1pwzf0p.cloudfront.net/shopLogos/stanley.png",
        alt: "Stanley",
        width: "tw:w-16",
        height: "tw:h-16",
      },
      {
        src: "https://d3vhsxl1pwzf0p.cloudfront.net/shopLogos/decathlon.png",
        alt: "Decathlon",
        width: "tw:w-20",
        height: "tw:h-20",
      },
      {
        src: "https://d3vhsxl1pwzf0p.cloudfront.net/shopLogos/sneakerstudio.png",
        alt: "Sneaker studio",
        width: "tw:w-24",
        height: "tw:h-20",
      },
    ],
    pt: [
      {
        src: "https://d3vhsxl1pwzf0p.cloudfront.net/shopLogos/xiaomi.svg",
        alt: "Mi Home",
        width: "tw:w-16",
        height: "tw:h-12",
      },
      {
        src: "https://d3vhsxl1pwzf0p.cloudfront.net/shopLogos/fellipe.webp",
        alt: "Fellipe Krein",
        width: "tw:w-32",
        height: "tw:h-12",
      },
      {
        src: "https://d3vhsxl1pwzf0p.cloudfront.net/shopLogos/kvra.png",
        alt: "KVRA",
        width: "tw:w-20",
        height: "tw:h-16",
      },
      {
        src: "https://d3vhsxl1pwzf0p.cloudfront.net/shopLogos/modalove.webp",
        alt: "Modalove",
        width: "tw:w-28",
        height: "tw:h-16",
      },
    ],
  } as const;

  const queryLang = getQueryLanguage();
  const available = Object.keys(logosConfig);
  const transLang = (trans._current_language || "").toLowerCase();
  const resolved =
    (queryLang && available.includes(queryLang) && queryLang) || (available.includes(transLang) && transLang) || "en";

  return logosConfig[resolved as keyof typeof logosConfig];
});

const animateNumber = (
  targetValue: number,
  currentRef: { value: number },
  duration: number = 2000,
  isDecimal: boolean = false
) => {
  const startValue = 0;
  const startTime = Date.now();

  const updateValue = () => {
    const elapsed = Date.now() - startTime;
    const progress = Math.min(elapsed / duration, 1);

    const easedProgress = 1 - Math.pow(1 - progress, 3);

    const currentValue = startValue + (targetValue - startValue) * easedProgress;

    if (isDecimal) {
      currentRef.value = parseFloat(currentValue.toFixed(1));
    } else {
      currentRef.value = Math.round(currentValue);
    }

    if (progress < 1) {
      requestAnimationFrame(updateValue);
    }
  };

  requestAnimationFrame(updateValue);
};

const parseMetricValue = (value: string): number => {
  if (!value) return 0;

  const cleanValue = value.replace(/[%+x]/g, "").trim();

  if (cleanValue.includes(".") || cleanValue.includes(",")) {
    return parseFloat(cleanValue.replace(",", "."));
  }

  return parseInt(cleanValue) || 0;
};

let observer: IntersectionObserver | null = null;

onMounted(async () => {
  await nextTick();

  if (containerRef.value) {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.3) {
            setTimeout(() => {
              const retentionValue = parseMetricValue(trans._register.platforms.aside.metrics.retention.value);
              animateNumber(retentionValue, animatedRetention, 2000);
            }, 200);

            setTimeout(() => {
              const aovValue = parseMetricValue(trans._register.platforms.aside.metrics.aov.value);
              animateNumber(aovValue, animatedAov, 2200);
            }, 400);

            setTimeout(() => {
              const moreProductsValue = parseMetricValue(trans._register.platforms.aside.metrics.moreProducts.value);
              animateNumber(moreProductsValue, animatedMoreProducts, 2400);
            }, 600);

            observer?.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.3,
        rootMargin: "0px 0px -50px 0px",
      }
    );

    observer.observe(containerRef.value);
  }
});

const asideTitleHtml = computed(() => {
  const title: string = trans._register.platforms.aside.title || "";
  return title.replace(/2500/g, "<b>2500</b>");
});

const formatRetentionValue = (value: number): string => {
  const originalValue = trans._register.platforms.aside.metrics.retention.value;
  if (originalValue.includes("%")) {
    return `${value}%`;
  }
  if (originalValue.includes("+")) {
    return `+${value}`;
  }
  if (originalValue.includes("x")) {
    return `${value}x`;
  }
  return value.toString();
};

const formatAovValue = (value: number): string => {
  const originalValue = trans._register.platforms.aside.metrics.aov.value;
  if (originalValue.includes("%")) {
    return `${value}%`;
  }
  if (originalValue.includes("+")) {
    return `+${value}`;
  }
  if (originalValue.includes("x")) {
    return `${value}x`;
  }
  return value.toString();
};

const formatMoreProductsValue = (value: number): string => {
  const originalValue = trans._register.platforms.aside.metrics.moreProducts.value;
  if (originalValue.includes("%")) {
    return `${value}%`;
  }
  if (originalValue.includes("+")) {
    return `+${value}`;
  }
  if (originalValue.includes("x")) {
    return `${value}x`;
  }
  return value.toString();
};

onUnmounted(() => {
  if (observer && containerRef.value) {
    observer.unobserve(containerRef.value);
    observer.disconnect();
    observer = null;
  }
});
</script>
